import { useRouter } from '@tanstack/react-router';
import type { WarehouseDataOptionTerms } from '@/features/warehouse-sync';
import { useRegisterBreadcrumbs } from '@/services/breadcrumbs';
import { generatePath } from '@/services/router/generate-path';
import { getWarehouseDestinationName } from '@/features/warehouse-sync';
import type { WarehouseDestination } from '@/services/db/warehouse-sync/entity';
import type { WarehouseConnector } from './types';

/**
 * The Integrations crumb points at the Ember URL so it resolves in both flag states — that page
 * has not migrated, and the `journeys` twin is the shape Ember serves.
 */
const EMBER_INTEGRATIONS_PATH = '/workspaces/:workspaceId/journeys/integrations';

const CONNECTOR_CRUMB_LABELS = {
  redshift: 'Redshift',
  bigquery: 'BigQuery',
  snowflake: 'Snowflake',
} as const satisfies Record<WarehouseConnector, string>;

const CONNECTOR_PATHS = {
  redshift: '/workspaces/$workspaceId/integrations/warehouse/redshift',
  bigquery: '/workspaces/$workspaceId/integrations/warehouse/bigquery',
  snowflake: '/workspaces/$workspaceId/integrations/warehouse/snowflake',
} as const satisfies Record<WarehouseConnector, string>;

/** "Integrations / Redshift", matching the crumbs the Ember routes registered. */
export function useWarehouseConnectorBreadcrumbs(workspaceId: string, connector: WarehouseConnector): void {
  const router = useRouter();

  useRegisterBreadcrumbs([
    {
      id: 'integrations',
      text: 'Integrations',
      href: generatePath(EMBER_INTEGRATIONS_PATH, { workspaceId }),
    },
    {
      id: 'integrations_warehouse_connector',
      text: CONNECTOR_CRUMB_LABELS[connector],
      href: router.buildLocation({ to: CONNECTOR_PATHS[connector], params: { workspaceId } }).href,
    },
  ]);
}

/** "Integrations / Amazon Simple Storage Service / S3" on the connected-config view. */
export function useWarehouseConfigBreadcrumbs(workspaceId: string, destination: WarehouseDestination): void {
  const router = useRouter();

  useRegisterBreadcrumbs([
    {
      id: 'integrations',
      text: 'Integrations',
      href: generatePath(EMBER_INTEGRATIONS_PATH, { workspaceId }),
    },
    {
      id: 'integrations_warehouse_config',
      text: getWarehouseDestinationName(destination),
      href: router.buildLocation({
        to: '/workspaces/$workspaceId/integrations/warehouse/$type',
        params: { workspaceId, type: destination },
      }).href,
      isTruncated: true,
    },
  ]);
}

/** The terms the export-option copy interpolates. */
export function useOptionTerms(): WarehouseDataOptionTerms {
  return {
    peoplePlural: 'people',
    peopleTitlePlural: 'People',
    personSingular: 'person',
    campaignSingular: 'automation',
    campaignTitleSingular: 'Automation',
  };
}
